import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, CircleCheck, Eye, Lock, RotateCcw } from 'lucide-react';
import { upsellClass, upsellIconClass } from 'components/portal/upsell';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { RenderedHtml } from 'modules/editor';
import {
  FIX_POINTS, useMarkLearned, useRetryMistake, useRevealMistake,
  type Mistake, type MistakeSkill, type MistakeAnswer, type RetryAnswer, type RetryForm,
} from 'modules/mistakes';
import { categorySurface, categoryTheme } from './categoryThemes';

/** Each skill's chip wording and the exam (hence accent) it belongs to. */
const SKILL_CHIP: Record<MistakeSkill, { key: string; exam: 'ielts' | 'sat' }> = {
  listening: { key: 'mistakes.chip_listening', exam: 'ielts' },
  reading: { key: 'mistakes.chip_reading', exam: 'ielts' },
  sat_rw: { key: 'mistakes.chip_sat_rw', exam: 'sat' },
  sat_math: { key: 'mistakes.chip_sat_math', exam: 'sat' },
};

const emptyAnswer = (form: RetryForm): RetryAnswer =>
  form.kind === 'multi' ? [] : form.kind === 'blanks' ? {} : '';

const isAnswered = (form: RetryForm, answer: RetryAnswer): boolean => {
  if (form.kind === 'multi') return Array.isArray(answer) && answer.length > 0;
  if (form.kind === 'blanks') {
    const values = answer as Record<string, string>;
    return form.blanks.every((id) => Boolean(values[id]?.trim()));
  }
  return typeof answer === 'string' && answer.trim().length > 0;
};

/** The same inputs a test uses, kept small: option buttons, a text box, or one box per blank. */
function RetryInput({ form, value, onChange, disabled }: {
  form: RetryForm; value: RetryAnswer; onChange: (next: RetryAnswer) => void; disabled: boolean;
}) {
  const { t } = useTranslation();
  if (form.kind === 'choice' || form.kind === 'multi') {
    const selected = new Set(Array.isArray(value) ? value : [value]);
    const toggle = (id: string) => {
      if (form.kind === 'choice') return onChange(id);
      const list = Array.isArray(value) ? value : [];
      onChange(list.includes(id) ? list.filter((v) => v !== id) : [...list, id]);
    };
    return (
      <div role="group" aria-label={t('mistakes.your_new_answer')} className="grid gap-2">
        {form.options.map((option) => {
          const on = selected.has(option.id);
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => toggle(option.id)}
              className={cn(
                'flex min-h-[46px] items-center gap-3 rounded-xl border px-3.5 py-2 text-left text-[14px] transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60',
                on ? 'border-foreground bg-muted text-foreground' : 'border-border bg-card hover:bg-muted/60',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center border',
                  form.kind === 'multi' ? 'rounded-[4px]' : 'rounded-full',
                  on ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/50',
                )}
              >
                {on && <Check className="h-3 w-3" />}
              </span>
              <RenderedHtml html={option.text} inline />
            </button>
          );
        })}
      </div>
    );
  }
  if (form.kind === 'blanks') {
    const values = (value as Record<string, string>) ?? {};
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {form.blanks.map((id, index) => (
          <Input
            key={id}
            aria-label={t('mistakes.blank_label', { n: index + 1 })}
            placeholder={t('mistakes.blank_label', { n: index + 1 })}
            value={values[id] ?? ''}
            disabled={disabled}
            maxLength={300}
            onChange={(e) => onChange({ ...values, [id]: e.target.value })}
          />
        ))}
      </div>
    );
  }
  return (
    <Input
      aria-label={t('mistakes.your_new_answer')}
      placeholder={t('mistakes.type_answer')}
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      maxLength={300}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Blurred stand-in for the explanation; the test's analysis (opened on its result page) shows the real one. */
function LockedExplanation({ attemptId }: { attemptId: string }) {
  const { t } = useTranslation();
  return (
    <section aria-label={t('mistakes.locked_aria')} className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none space-y-2.5 p-4 blur-[5px]">
        <i className="block h-[11px] w-[90%] rounded-md bg-border" />
        <i className="block h-[11px] w-[74%] rounded-md bg-border" />
        <i className="block h-[11px] w-[58%] rounded-md bg-border" />
      </div>
      <div className="relative flex min-h-[88px] flex-col items-center justify-center gap-3 bg-gradient-to-r from-card/95 to-card/70 px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
          <span className={cn(upsellIconClass, 'h-[34px] w-[34px] rounded-[10px]')}>
            <Lock className="h-[17px] w-[17px]" aria-hidden />
          </span>
          <div>
            <p className="text-sm font-bold text-foreground">{t('mistakes.locked_title')}</p>
            <p className="text-[12.5px] text-muted-foreground">{t('mistakes.explanation_locked')}</p>
          </div>
        </div>
        <Link
          to={`/portal/results/${attemptId}`}
          className={cn('inline-flex h-10 shrink-0 items-center rounded-xl px-5 text-sm font-bold', upsellClass)}
        >
          {t('mistakes.unlock_test')}
        </Link>
      </div>
    </section>
  );
}

/** The right answer and, when the attempt's analysis is unlocked, the author's explanation. */
function AnswerReveal({ answer, attemptId }: { answer: MistakeAnswer; attemptId: string }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="rounded-md border border-success-200 bg-success-50 px-3 py-2 text-[13.5px] dark:border-success-900/50 dark:bg-success-900/20">
        <p className="text-[12px] font-medium text-success-700 dark:text-success-400">{t('mistakes.correct_answer')}</p>
        <p className="mt-0.5 font-medium text-foreground">{answer.correctAnswer ? <RenderedHtml html={answer.correctAnswer} inline /> : '—'}</p>
      </div>
      {answer.explanation && (
        <div className="rounded-md bg-muted/60 px-3 py-2.5 text-[13.5px] leading-[1.55] text-foreground/90">
          <RenderedHtml html={answer.explanation} />
        </div>
      )}
      {answer.explanationLocked && <LockedExplanation attemptId={attemptId} />}
    </>
  );
}

function SkillChip({ skill }: { skill: MistakeSkill }) {
  const { t } = useTranslation();
  const { key, exam } = SKILL_CHIP[skill];
  const { color, background } = categorySurface(categoryTheme(exam).accent);
  return (
    <span style={{ color, background }} className="inline-flex rounded-[7px] px-2.5 py-1 text-[11px] font-extrabold tracking-[0.04em]">
      {t(key)}
    </span>
  );
}

/**
 * One mistake to put right: answer it again first; a right answer fixes it (and earns points if the answer
 * was not revealed). Revealing is the way out, and then it can be marked learned.
 */
export default function MistakeCard({ mistake }: { mistake: Mistake }) {
  const { t } = useTranslation();
  const retry = useRetryMistake();
  const reveal = useRevealMistake();
  const markLearned = useMarkLearned();
  const [answer, setAnswer] = useState<RetryAnswer>(() => (mistake.retry ? emptyAnswer(mistake.retry) : ''));
  const [wrongTries, setWrongTries] = useState(0);
  const result = retry.data?.correct ? retry.data : null;
  const busy = retry.isPending || reveal.isPending;

  const check = () => {
    if (!mistake.retry || !isAnswered(mistake.retry, answer)) return;
    retry.mutate({ mistakeKey: mistake.key, answer }, {
      onSuccess: (r) => { if (!r.correct) setWrongTries((n) => n + 1); },
    });
  };

  return (
    <li className={cn('rounded-[22px] border bg-card transition-colors', result ? 'border-success-300 dark:border-success-800' : 'border-border')}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-5 pt-5 md:px-6">
        {mistake.skill ? <SkillChip skill={mistake.skill} /> : <span />}
        <span className="min-w-0 break-words text-[12.5px] text-muted-foreground">
          {mistake.testTitle}
          {mistake.passageTitle ? ` · ${mistake.passageTitle}` : ''}
          {mistake.answeredAt && (
            <>
              {' · '}
              <time dateTime={mistake.answeredAt}>{new Date(mistake.answeredAt).toLocaleDateString()}</time>
            </>
          )}
        </span>
      </div>
      <div className="space-y-3.5 px-5 pb-5 pt-3 md:px-6">
        <RenderedHtml html={mistake.prompt} className="text-base font-semibold leading-6 text-foreground" />
        <p className="text-[13px] text-muted-foreground">
          {t('mistakes.your_answer')}:{' '}
          <span className="font-medium text-error-700 line-through decoration-error-400/70 dark:text-error-400">
            {mistake.yourAnswer ? <RenderedHtml html={mistake.yourAnswer} inline /> : '—'}
          </span>
        </p>

        {result ? (
          <>
            <p role="status" className="flex items-center gap-2 text-[14px] font-semibold text-success-700 dark:text-success-400">
              <CircleCheck className="h-4 w-4" aria-hidden />
              {result.pointsAwarded > 0 ? t('mistakes.fixed_points', { points: result.pointsAwarded }) : t('mistakes.fixed')}
            </p>
            <AnswerReveal answer={result} attemptId={mistake.attemptId} />
          </>
        ) : mistake.revealed ? (
          <AnswerReveal answer={mistake} attemptId={mistake.attemptId} />
        ) : mistake.retry ? (
          <form
            className="space-y-2.5"
            onSubmit={(e) => { e.preventDefault(); check(); }}
          >
            <RetryInput form={mistake.retry} value={answer} onChange={setAnswer} disabled={busy} />
            {wrongTries > 0 && (
              <p role="status" className="text-[13px] font-medium text-warning-700 dark:text-warning-400">
                {t('mistakes.still_wrong', { count: wrongTries })}
              </p>
            )}
            {(retry.isError || reveal.isError) && (
              <p role="alert" className="text-[13px] text-error-700 dark:text-error-400">{t('mistakes.action_failed')}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                size="sm"
                className="gap-1.5 bg-foreground text-background hover:bg-foreground/90"
                disabled={busy || !isAnswered(mistake.retry, answer)}
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                {t('mistakes.check')}
              </Button>
              <span className="inline-flex items-center rounded-full bg-[#FFF4D6] px-3 py-1 text-xs font-extrabold text-[#92400E] dark:bg-amber-400/15 dark:text-amber-300">
                {t('mistakes.points_badge', { points: FIX_POINTS })}
              </span>
              <Button type="button" variant="outline" size="sm" className="ml-auto gap-1.5" disabled={busy} onClick={() => reveal.mutate(mistake.key)}>
                <Eye className="h-4 w-4" aria-hidden />
                {t('mistakes.show_answer')}
              </Button>
            </div>
            <p className="text-[12px] text-muted-foreground">{t('mistakes.reveal_note')}</p>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-[13px] text-muted-foreground">{t('mistakes.reveal_only')}</p>
            {reveal.isError && <p role="alert" className="text-[13px] text-error-700 dark:text-error-400">{t('mistakes.action_failed')}</p>}
            <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => reveal.mutate(mistake.key)}>
              <Eye className="h-4 w-4" aria-hidden />
              {t('mistakes.show_answer')}
            </Button>
          </div>
        )}
      </div>
      {mistake.revealed && !result && (
        <div className="flex justify-end px-5 pb-5 md:px-6">
          <Button
            size="sm"
            className="gap-1.5 bg-foreground text-background hover:bg-foreground/90"
            disabled={markLearned.isPending}
            onClick={() => markLearned.mutate(mistake.key)}
          >
            <Check className="h-4 w-4" aria-hidden />
            {t('mistakes.mark_learned')}
          </Button>
        </div>
      )}
    </li>
  );
}
